/**
 * {@link ConfirmDialog} — a simple modal confirmation dialog for destructive actions.
 *
 * Uses the native `<dialog>` element for accessibility (focus trapping, Escape
 * to close, `aria-modal`). Renders a message and two buttons: cancel and confirm.
 */

import { useEffect, useRef } from 'react';
import { Button } from '@scoring/frontend-shared';
import type { CSSProperties } from 'react';

export interface ConfirmDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Title shown in the dialog header. */
  title: string;
  /** Descriptive message shown in the dialog body. */
  message: string;
  /** Label for the confirm button. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Label for the cancel button. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Called when the user confirms the action. */
  onConfirm: () => void;
  /** Called when the user cancels (or presses Escape). */
  onCancel: () => void;
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0, 0, 0, 0.4)',
  zIndex: 1000,
};

const dialogStyle: CSSProperties = {
  background: 'var(--color-bg-surface, #FFFFFF)',
  borderRadius: 'var(--radius-button, 8px)',
  padding: '1.5rem',
  maxWidth: '24rem',
  width: '90%',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
};

const titleStyle: CSSProperties = {
  margin: '0 0 0.75rem',
  fontSize: '1.125rem',
  fontWeight: 600,
};

const messageStyle: CSSProperties = {
  margin: '0 0 1.5rem',
  color: 'var(--color-text-secondary, #6B7280)',
  lineHeight: 1.5,
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  gap: '0.75rem',
  justifyContent: 'flex-end',
};

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      // Focus the dialog container when opened
      dialogRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        onCancel();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div style={overlayStyle} aria-hidden="true" onClick={onCancel}>
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-message"
        tabIndex={-1}
        style={dialogStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="confirm-dialog-title" style={titleStyle}>
          {title}
        </h2>
        <p id="confirm-dialog-message" style={messageStyle}>
          {message}
        </p>
        <div style={actionsStyle}>
          <Button variant="secondary" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button onClick={onConfirm}>{confirmLabel}</Button>
        </div>
      </div>
    </div>
  );
}
